import { useEffect, useMemo, useRef, useState, useCallback } from "react";

import type { Props } from "./types";

import { PdfViewer } from "../pdfViewer";
import { Spinner } from "../spinner";
import style from "./style.module.scss";

export function PdfDocumentReview(props: Props) {
	const { document, loading, ...inputProps } = props;
	const { id, value, changeHandler } = inputProps;
	const documentRef = useRef<HTMLDivElement>();
	const [reviewed, setReviewed] = useState(value);
	const [pdfRendered, setPdfRendered] = useState(false);

	const onScroll = useCallback(
		(element = documentRef.current) => {
			//If already read, no need to keep tracking scrolling
			if (reviewed) return;
			//If the document is not loaded, no need to keep tracking scrolling
			if (!element?.clientHeight) return;
			if (loading) return;
			if (!pdfRendered) return;

			// Check if you have scrolled to bottom, update reviewed if true
			const scrollPos = element.scrollHeight - Math.round(element.scrollTop);
			const scrolledToBottom = scrollPos <= element.clientHeight + 1;

			if (scrolledToBottom) {
				setReviewed(true);
			}
		},
		[reviewed, loading, pdfRendered],
	);

	useEffect(() => {
		onScroll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document, loading, pdfRendered]);

	useEffect(() => {
		if (props.value === reviewed) return;
		changeHandler(!!reviewed, id);
	}, [changeHandler, reviewed, id, props.value]);

	const fixedDocument = useMemo(() => {
		return `data:application/pdf;base64,${document}`;
	}, [document]);

	if (loading) {
		return <Spinner tag="h5">Loading Document</Spinner>;
	}

	return (
		<div className={style.documentDisplay} onScroll={() => onScroll()} ref={documentRef}>
			<PdfViewer file={fixedDocument} allPages onRenderSuccess={() => setPdfRendered(true)} onScroll={onScroll} />
		</div>
	);
}
